import Image from "next/image";
import { useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, Play, X, ChevronLeft, ChevronRight, ArrowRight, Calendar } from "lucide-react";

const EventCard = ({ event, index, theme }) => {
  const [lightbox, setLightbox] = useState(null);
  const formattedDate = new Date(event.event_date).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const photos = event.photos || [];
  const video = event.video;

  const openLightbox = (type, idx = 0, customPhotos = null) => setLightbox({ type, idx, customPhotos });
  const closeLightbox = () => setLightbox(null);

  const activePhotos = lightbox?.customPhotos || photos;

  return (
    <>
      <article className="group relative flex flex-col xl:flex-row !gap-8 min-[2560px]:!gap-20 w-full bg-transparent">
        <div className="w-full md:w-[40%] min-[2560px]:w-[42%] relative aspect-[16/10] rounded-[10px] min-[2560px]:rounded-[32px] overflow-hidden shrink-0 shadow-2xl bg-background-secondary">
          <div
            className={`absolute bottom-4 right-4 min-[2560px]:bottom-10 min-[2560px]:right-10 z-20 !px-3 !py-1 min-[2560px]:!px-6 min-[2560px]:!py-3 rounded-lg min-[2560px]:rounded-2xl ${theme?.badge || "bg-purple-600"} text-white font-bold text-sm min-[2560px]:text-2xl shadow-lg`}
          >
            {String(index + 1).padStart(2, "0")}
          </div>
          <Image src={photos[0]} alt={event.title} fill className="object-cover object-left transition-transform duration-700 group-hover:scale-110" />
        </div>

        <div className="flex-1 flex flex-col !pt-2 min-[2560px]:!pt-6">
          <div className="flex items-start justify-between !gap-4 !mb-3 min-[2560px]:!mb-8">
            <h3 className="text-md md:text-xl min-[2560px]:text-4xl font-semibold text-text-primary leading-tight">{event.title}</h3>
            <span className="shrink-0 !px-2.5 !py-1 min-[2560px]:!px-5 min-[2560px]:!py-2 rounded-md bg-surface border border-border-primary text-[11px] min-[2560px]:text-[18px] font-light text-secondary uppercase tracking-wider">
              {photos.length} Photos
            </span>
          </div>

          <div className="flex flex-wrap items-center !gap-5 min-[2560px]:!gap-12 text-sm min-[2560px]:text-2xl text-secondary !mb-5 min-[2560px]:!mb-12">
            <div className="flex items-center !gap-1.5 min-[2560px]:!gap-4">
              <MapPin className="w-4 h-4 min-[2560px]:w-8 min-[2560px]:h-8 text-text-secondary" />
              {event.place}
            </div>
            <div className="flex items-center !gap-1.5 min-[2560px]:!gap-4">
              <Calendar className="w-4 h-4 min-[2560px]:w-8 min-[2560px]:h-8 text-text-secondary" />
              {formattedDate}
            </div>
          </div>

          <p className="text-secondary text-[15px] min-[2560px]:text-[24px] leading-relaxed !mb-8 min-[2560px]:!mb-16 line-clamp-3 font-light">{event.description}</p>

          <div className="flex flex-wrap items-center !gap-4 min-[2560px]:!gap-10 mt-auto">
            {(video || photos.length > 0) && (
              <button
                onClick={() => openLightbox(video ? "video" : "photo", 0)}
                className={`flex items-center !gap-3 min-[2560px]:!gap-6 !px-6 !py-3 cursor-pointer min-[2560px]:!px-12 min-[2560px]:!py-6 rounded-xl min-[2560px]:rounded-[24px] ${theme?.badge} text-white text-sm min-[2560px]:text-2xl font-semibold hover:scale-105 transition-transform`}
              >
                Watch Highlight <Play className="w-4 h-4 min-[2560px]:w-7 min-[2560px]:h-7 fill-current" />
              </button>
            )}

            {photos.length > 1 && (
              <button
                onClick={() => openLightbox("photo", 0, photos.slice(1))}
                className="flex items-center !gap-2 min-[2560px]:!gap-4 !px-6 !py-3 cursor-pointer min-[2560px]:!px-12 min-[2560px]:!py-6 rounded-xl min-[2560px]:rounded-[24px] bg-surface border border-border-primary text-text-primary text-sm min-[2560px]:text-2xl font-semibold hover:bg-surface-secondary/70 transition-colors"
              >
                More Moments <ArrowRight className="w-4 h-4 min-[2560px]:w-7 min-[2560px]:h-7" />
              </button>
            )}
          </div>
        </div>
      </article>

      {lightbox &&
        createPortal(
          <div className="fixed inset-0 z-[99999] bg-black/95 backdrop-blur-2xl flex items-center justify-center !p-3 md:!p-6" onClick={closeLightbox}>
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>
            {lightbox.type === "photo" && (
              <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <Image src={activePhotos[lightbox.idx]} alt="" width={1800} height={1200} className="max-w-full max-h-full object-contain rounded-2xl" priority />

                {activePhotos.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setLightbox((l) => ({
                          ...l,
                          idx: (l.idx - 1 + activePhotos.length) % activePhotos.length,
                        }))
                      }
                      className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all cursor-pointer"
                    >
                      <ChevronLeft className="w-7 h-7" />
                    </button>

                    <button
                      onClick={() =>
                        setLightbox((l) => ({
                          ...l,
                          idx: (l.idx + 1) % activePhotos.length,
                        }))
                      }
                      className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all cursor-pointer"
                    >
                      <ChevronRight className="w-7 h-7" />
                    </button>
                  </>
                )}
              </div>
            )}

            {lightbox.type === "video" && (
              <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <video src={video} controls autoPlay className="max-w-full max-h-full rounded-2xl bg-black" />
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default EventCard;
